export interface PromptFieldAttachment {
  name: string;
  url: string;
  size?: number;
}

const isAttachmentLike = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const coerceNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
};

/**
 * Normalize persisted attachment data into a standard array structure.
 *
 * The prompt_fields.attachments column is expected to be jsonb, but historical
 * records (or manual SQL updates) may have stored JSON strings. This helper
 * tolerates both formats so that the UI always receives a consistent array.
 */
export const normalizeAttachments = (raw: unknown): PromptFieldAttachment[] => {
  if (!raw) {
    return [];
  }

  let value: unknown = raw;

  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter(isAttachmentLike)
    .map((item) => {
      const name = typeof item.name === 'string' ? item.name : '';
      const url = typeof item.url === 'string' ? item.url : '';
      const size = coerceNumber((item as Record<string, unknown>).size);

      if (!name && !url) {
        return null;
      }

      return {
        name: name || url,
        url,
        ...(size !== undefined ? { size } : {}),
      } as PromptFieldAttachment;
    })
    .filter((item): item is PromptFieldAttachment => item !== null && item.url.length > 0);
};

